
import { Component, Vue, Watch } from 'vue-property-decorator';
import { DiscountModel } from '@/models/discount';
import { StoreModel } from '@/models/store';
import PackageConfigBuilder from '@/components/PackageConfigBuilder.vue';

@Component({
  components: {
    PackageConfigBuilder,
  },
})
export default class DiscountNewPage extends Vue {
  public isLoading: boolean = true;
  public discount: DiscountModel = new DiscountModel();
  public messageError: string = '';
  public isError: boolean = false;
  public merchantLoading: boolean = false;
  public targetedMerchant: StoreModel | null = null;
  public merchantLoadTimeout: number | null = null;
  public merchantTargetingType: string = 'all';

  private discountTypeMapping: { [key: string]: string } = {
    'NO_DISCOUNT': 'No Discount (Package Override Only)',
    'PERCENTAGE_DISCOUNT': 'Percentage Discount',
    'FIXED_DISCOUNT_AMOUNT': 'Fixed Discount Amount',
    'FIXED_PRICE': 'Fixed Price'
  };

  get discountTypeOptions() {
    return Object.keys(this.discountTypeMapping).map(key => ({
      value: key,
      text: this.discountTypeMapping[key]
    }));
  }

  get discountSummary(): string {
    if (!this.discount.type) return 'No discount type selected';
    
    const typeLabel = this.discountTypeMapping[this.discount.type] || this.discount.type;
    let value = '';
    
    switch (this.discount.type) {
      case 'NO_DISCOUNT':
        value = 'Package overrides only - no price reduction';
        break;
      case 'PERCENTAGE_DISCOUNT':
        value = (this.discount.rate !== null && this.discount.rate !== undefined) ? `${this.discount.rate}%` : 'No rate set';
        break;
      case 'FIXED_DISCOUNT_AMOUNT':
        value = this.discount.fixedDiscountAmount ? `$${this.discount.fixedDiscountAmount}` : 'No amount set';
        break;
      case 'FIXED_PRICE':
        value = this.discount.fixedPrice ? `$${this.discount.fixedPrice}` : 'No price set';
        break;
    }
    
    return `${typeLabel}: ${value}`;
  }

  get hasMerchantTargeting(): boolean {
    return !!(this.discount.merchantId || (this.discount.merchantNames && this.discount.merchantNames.length > 0));
  }

  get merchantTargetingSummary(): string {
    if (!this.hasMerchantTargeting) return 'Available to all merchants';
    
    const parts = [];
    if (this.discount.merchantId) parts.push(`ID: ${this.discount.merchantId}`);
    if (this.discount.merchantNames && this.discount.merchantNames.length > 0) {
      parts.push(`Names: ${this.discount.merchantNames.join(', ')}`);
    }
    
    return parts.join(' | ');
  }

  get hasPackageOverride(): boolean {
    return !!(this.discount.packageConfig && Object.keys(this.discount.packageConfig).length > 0);
  }

  get packageOverrideSummary(): string {
    if (!this.hasPackageOverride) return 'No package overrides configured';
    
    const config = this.discount.packageConfig;
    const parts = [];
    
    if (config.billingPackageId) parts.push(`Package: ${config.billingPackageId}`);
    if (config.interval) parts.push(`Interval: ${config.interval}`);
    if (config.trialEnabled) parts.push(`Trial: ${config.trialDays} days`);
    
    return parts.length > 0 ? parts.join(' | ') : 'Package overrides configured';
  }

  public async created() {
    // Initialize targeting type based on existing data
    if (this.discount.merchantId) {
      this.merchantTargetingType = 'installed';
    } else if (this.discount.merchantNames && this.discount.merchantNames.length > 0) {
      this.merchantTargetingType = 'new';
    } else {
      this.merchantTargetingType = 'all';
    }
  }

  public async createDiscount() {
    await this.discount.save();
    await this.$router.push('/discounts');
  }

  public async loadTargetedMerchant(merchantId: number) {
    if (!merchantId) {
      this.targetedMerchant = null;
      return;
    }

    try {
      this.merchantLoading = true;
      const store = new StoreModel();
      store.id = merchantId;
      await store.fetch();
      this.targetedMerchant = store;
    } catch (error) {
      console.error('Failed to load merchant:', error);
      this.targetedMerchant = null;
    } finally {
      this.merchantLoading = false;
    }
  }

  @Watch('discount.merchantId')
  public async onMerchantIdChange(newMerchantId: number | null) {
    // Clear existing timeout
    if (this.merchantLoadTimeout) {
      clearTimeout(this.merchantLoadTimeout);
    }

    if (newMerchantId) {
      // Set 2-second delay before loading merchant info
      this.merchantLoadTimeout = setTimeout(async () => {
        await this.loadTargetedMerchant(newMerchantId);
      }, 2000);
    } else {
      this.targetedMerchant = null;
    }
  }

  @Watch('merchantTargetingType')
  public onMerchantTargetingTypeChange(newType: string) {
    // Clear all targeting fields when switching types
    if (newType === 'all') {
      this.discount.merchantId = null;
      this.discount.merchantNames = [];
      this.targetedMerchant = null;
    } else if (newType === 'installed') {
      this.discount.merchantNames = [];
    } else if (newType === 'new') {
      this.discount.merchantId = null;
      this.targetedMerchant = null;
    }
  }
}
